/**
 * Where things stand: the registry, the lines this org captured as a site, and
 * this month's state for every enrollment it tracks.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ArrowRight, Building2, Camera, ClipboardCheck, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { StatTile, StatTileGrid, PageState } from '../weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { verifyApi, type Overview, type StatementState, VerifyApiError } from './api';
import { StatementStateBadge, errMessage, fmtHours, monthName } from './shared';

const STATE_ORDER: StatementState[] = [
  'complete',
  'meets_needs_evidence',
  'awaiting_confirmation',
  'in_progress',
  'at_risk',
  'short_at_deadline',
];

export function VerifyOverview({ onNavigate }: { onNavigate: (tab: string) => void }) {
  const { t, language } = useAppTranslation();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await verifyApi.overview());
    } catch (e) {
      if (e instanceof VerifyApiError && e.status === 403) setDenied(true);
      else setError(errMessage(e, t('verify.overview.load_failed', 'Could not load Verify.')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (denied) {
    return (
      <PageState
        variant="denied"
        title={t('verify.not_enabled_title', 'Verify is not turned on')}
        message={t('verify.not_enabled', 'Verify is not enabled for this organization.')}
      />
    );
  }
  if (loading && !data) return <PageState variant="loading" />;
  if (error && !data) {
    return (
      <PageState
        variant="error"
        message={error}
        action={
          <Button variant="outline" size="sm" onClick={() => void load()}>
            {t('verify.retry', 'Try again')}
          </Button>
        }
      />
    );
  }
  if (!data) return null;

  const states = data.enrollments.this_month || {};

  const steps = [
    {
      icon: Building2,
      title: t('verify.overview.step_registry', 'Qualify an organization once'),
      body: t(
        'verify.overview.step_registry_body',
        'Basis, evidence, oversight, contact and activity types — each confirmed by a named person. Every Verify organization reads the same registry.',
      ),
      tab: 'registry',
    },
    {
      icon: Camera,
      title: t('verify.overview.step_capture', 'Capture hours where they happen'),
      body: t(
        'verify.overview.step_capture_body',
        'Staff entry, a QR self check-in, or a photo of the paper sign-in sheet. Nothing is filed until a person at the organization confirms it.',
      ),
      tab: 'capture',
    },
    {
      icon: ClipboardCheck,
      title: t('verify.overview.step_records', 'Confirm, and answer disputes'),
      body: t(
        'verify.overview.step_records_body',
        'Members see every line with who filed it and can dispute any one. A disputed line stops counting until it is resolved.',
      ),
      tab: 'records',
    },
    {
      icon: ShieldCheck,
      title: t('verify.overview.step_medicaid', 'Deliver into Medicaid'),
      body: t(
        'verify.overview.step_medicaid_body',
        'Match lines to enrollments, deliver the month into the Medicaid work record, and see whether the next redetermination has its evidence.',
      ),
      tab: 'medicaid',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-ink-500">
          {t('verify.overview.as_of', 'This month: {{month}}', { month: monthName(data.year, data.month, language) })}
        </div>
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {t('verify.refresh', 'Refresh')}
        </Button>
      </div>

      <StatTileGrid>
        <StatTile
          label={t('verify.overview.registry', 'Qualified organizations')}
          value={data.registry.qualified}
          caption={t('verify.overview.registry_caption', '{{pending}} in review · {{total}} in registry', {
            pending: data.registry.pending,
            total: data.registry.total,
          })}
          tone="accent"
        />
        <StatTile
          label={t('verify.overview.awaiting', 'Awaiting confirmation')}
          value={data.as_site.awaiting_confirmation}
          caption={t('verify.overview.awaiting_caption', 'QR check-ins at your sites')}
          tone={data.as_site.awaiting_confirmation ? 'attention' : 'neutral'}
        />
        <StatTile
          label={t('verify.overview.confirmed', 'Confirmed at your sites')}
          value={fmtHours(data.as_site.confirmed_hours)}
          caption={t('verify.overview.confirmed_caption', '{{n}} lines · {{sheets}} from paper sheets · {{qr}} by QR', {
            n: data.as_site.confirmed,
            sheets: data.as_site.from_sheets,
            qr: data.as_site.from_qr,
          })}
          tone="success"
        />
        <StatTile
          label={t('verify.overview.disputes', 'Open disputes')}
          value={data.disputes_open}
          caption={t('verify.overview.disputes_caption', 'Raised by members')}
          tone={data.disputes_open ? 'danger' : 'neutral'}
        />
        <StatTile
          label={t('verify.overview.tracked', 'Enrollments tracked')}
          value={data.enrollments.tracked}
          caption={t('verify.overview.tracked_caption', 'Work requirement applies')}
        />
      </StatTileGrid>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('verify.overview.month_states', 'Members this month')}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.enrollments.tracked === 0 ? (
            <p className="text-sm text-ink-500">
              {t(
                'verify.overview.no_enrollments',
                'No enrollments are tracked yet. Open the Medicaid tab and track a member.',
              )}
            </p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {STATE_ORDER.filter((s) => states[s]).map((s) => (
                <div key={s} className="flex items-center gap-2 rounded-md border border-v2border px-3 py-2">
                  <StatementStateBadge state={s} t={t} />
                  <span className="font-display text-lg font-semibold text-ink">{states[s]}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {steps.map((s, i) => (
          <button
            key={s.tab}
            type="button"
            onClick={() => onNavigate(s.tab)}
            className="group rounded-[10px] border border-v2border bg-surface p-4 text-left shadow-v2-1 transition hover:border-v2accent"
          >
            <div className="flex items-center gap-2 text-v2accent-ink">
              <span className="font-mono text-xs">{String(i + 1).padStart(2, '0')}</span>
              <s.icon className="h-4 w-4" />
            </div>
            <div className="mt-2 font-medium text-ink">{s.title}</div>
            <p className="mt-1 text-sm text-ink-500">{s.body}</p>
            <div className="mt-3 inline-flex items-center text-sm text-v2accent-ink">
              {t('verify.open', 'Open')}
              <ArrowRight className="ml-1 h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
